/**
 * Player prayer presence art with intensity stages (like lies):
 *   /art/prayers/{defId}-{1|2|3}.webp
 * Falls back to verse plate /art/cards/{defId}.webp if stage missing.
 */

import {
  bindArtImage,
  isArtKnownMissing,
  markArtMissing,
  markArtOk,
} from "./artCache";
import { cardArtSrc } from "./cardFace";

export function prayerArtStage(level: number): 1 | 2 | 3 {
  const n = Math.round(level);
  if (n >= 3) return 3;
  if (n <= 1) return 1;
  return 2;
}

export function prayerArtSrc(defId: string, level: number): string {
  return `/art/prayers/${defId}-${prayerArtStage(level)}.webp`;
}

/** Build a compact art frame for board presence. */
export function createPrayerArt(opts: {
  defId: string;
  level: number;
  name?: string;
  category?: string;
  large?: boolean;
}): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className =
    "prayer-art" +
    (opts.large ? " prayer-art-large" : "") +
    (opts.category ? ` cat-${opts.category}` : "");
  wrap.dataset.defId = opts.defId;
  wrap.dataset.stage = String(prayerArtStage(opts.level));

  const stage = prayerArtStage(opts.level);
  wrap.style.setProperty("--prayer-stage", String(stage));
  wrap.classList.add(`stage-${stage}`);

  const placeholder = document.createElement("div");
  placeholder.className = "prayer-art-placeholder";
  placeholder.setAttribute("aria-hidden", "true");
  const mono = document.createElement("span");
  mono.className = "prayer-art-monogram";
  mono.textContent = monogramFromId(opts.defId);
  placeholder.appendChild(mono);
  wrap.appendChild(placeholder);

  const stageSrc = prayerArtSrc(opts.defId, opts.level);
  const plateSrc = cardArtSrc(opts.defId);

  const tryAttach = (src: string, isFallback: boolean): void => {
    if (isArtKnownMissing(src)) {
      if (!isFallback) tryAttach(plateSrc, true);
      else wrap.classList.add("no-art");
      return;
    }
    const img = document.createElement("img");
    img.className = "prayer-art-img";
    img.alt = opts.name ? `${opts.name} art` : "";
    img.loading = "lazy";
    img.decoding = "async";
    img.draggable = false;
    if (isFallback) img.dataset.fallback = "1";
    bindArtImage(img, src, () => {
      img.remove();
      if (!isFallback) tryAttach(plateSrc, true);
      else wrap.classList.add("no-art");
    });
    wrap.appendChild(img);
  };

  tryAttach(stageSrc, false);
  return wrap;
}

/** Swap plate when prayer intensity steps down/up. */
export function updatePrayerArtEl(
  root: HTMLElement,
  defId: string,
  level: number,
): void {
  const art = root.querySelector(".prayer-art") as HTMLElement | null;
  if (!art) return;
  const stage = prayerArtStage(level);
  if (art.dataset.stage === String(stage) && art.dataset.defId === defId) return;
  art.dataset.defId = defId;
  art.dataset.stage = String(stage);
  art.classList.remove("stage-1", "stage-2", "stage-3");
  art.classList.add(`stage-${stage}`);
  art.style.setProperty("--prayer-stage", String(stage));

  const stageSrc = prayerArtSrc(defId, level);
  const plateSrc = cardArtSrc(defId);
  const existing = art.querySelector(".prayer-art-img") as HTMLImageElement | null;

  const attach = (src: string, isFallback: boolean): void => {
    if (isArtKnownMissing(src)) {
      if (!isFallback) attach(plateSrc, true);
      else {
        existing?.remove();
        art.classList.add("no-art");
      }
      return;
    }
    art.classList.remove("no-art");
    let img = existing;
    if (!img) {
      img = document.createElement("img");
      img.className = "prayer-art-img";
      img.alt = "";
      img.loading = "lazy";
      img.decoding = "async";
      img.draggable = false;
      art.appendChild(img);
    }
    img.dataset.fallback = isFallback ? "1" : "";
    bindArtImage(img, src, () => {
      if (!isFallback) attach(plateSrc, true);
      else {
        img?.remove();
        art.classList.add("no-art");
      }
    });
  };

  attach(stageSrc, false);
}

function monogramFromId(id: string): string {
  const parts = id.split("-").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]!.slice(0, 2) + parts[1]!.slice(0, 1)).toUpperCase();
  }
  return id.replace(/[^a-z]/gi, "").slice(0, 3).toUpperCase() || "P";
}

export function preloadPrayerArt(
  defIds: string[],
  levels: number[] = [1, 2, 3],
): void {
  for (const id of defIds) {
    for (const lv of levels) {
      const src = prayerArtSrc(id, lv);
      if (isArtKnownMissing(src)) continue;
      const img = new Image();
      img.onload = () => markArtOk(src);
      img.onerror = () => markArtMissing(src);
      img.src = src;
    }
    const plate = cardArtSrc(id);
    if (isArtKnownMissing(plate)) continue;
    const fallback = new Image();
    fallback.onload = () => markArtOk(plate);
    fallback.onerror = () => markArtMissing(plate);
    fallback.src = plate;
  }
}
